/**
 * Together Health — AI Product Recommender
 *
 * Drives the multi-step quiz, calls the Cloudflare Worker proxy, and renders
 * the personalised results.  Reuses the store's existing cart API for checkout.
 */

(function () {
  "use strict";

  // -----------------------------------------------------------------------
  // Config — the worker URL is injected by the Liquid section schema setting
  // -----------------------------------------------------------------------
  var WORKER_URL = (function () {
    var el = document.querySelector("[data-worker-url]");
    return el ? el.getAttribute("data-worker-url") : "";
  })();

  // -----------------------------------------------------------------------
  // State
  // -----------------------------------------------------------------------
  var profile = {
    health_confirmed: null,
    gender: null,
    age_range: null,
    life_stage: null,
    menopause_periods_stopped: null,
    menopause_symptoms_now: null,
    diet: null,
    diet_other: "",
    fruit_veg_portions: null,
    eats_dairy: null,
    oily_fish_per_week: null,
    health_goals: [],
    symptoms: [],
    nutrient_concerns: [],
    current_supplements: "",
    free_text: "",
    order_mode: "subscription",
  };

  var products = []; // loaded from Liquid-generated JSON
  var promptConfig = null; // loaded from metaobject JSON (persona + rules)
  var selectedItems = []; // product handles user has toggled on
  var recommendations = null; // the AI response
  var stepHistory = []; // for back navigation
  var resultsToken = null; // funnel-row UUID: set at /start, echoed by the AI call (X-Recommender-Token); embedded in the shareable results URL
  // Traffic channel from the ?source= param (e.g. "chat" for Intercom links);
  // defaults to "website". Captured synchronously at script-parse time — the
  // theme's scroll-spy (main.js) rewrites the URL and drops the query string on
  // DOMContentLoaded, so reading it in init() can race and miss it. Persist to
  // sessionStorage so a mid-funnel reload (URL no longer has the param) still
  // attributes to the right channel.
  var sessionSource = (function () {
    var cleaned = "";
    try {
      cleaned = (new URLSearchParams(window.location.search).get("source") || "")
        .toLowerCase().replace(/[^a-z0-9_-]/g, "").slice(0, 32);
    } catch (e) {}
    try {
      if (cleaned) sessionStorage.setItem("th_rec_source", cleaned);
      else cleaned = sessionStorage.getItem("th_rec_source") || "";
    } catch (e) {}
    return cleaned || "website";
  })();
  var consentId = null; // UUID from POST /consent; the worker requires it on the AI call
  var consentPromise = null; // in-flight /consent request, awaited at submit time
  var resultsRendered = false; // true once the results screen has actually rendered (post-disclaimer or rehydrate)
  var resultsViewedSent = false; // guards POST /viewed so we only stamp the submission once
  var loadingMessageTimer = null; // interval id for the rotating loading copy

  // The order of quiz steps and the rules for which step follows which
  var STEP_FLOW = ["gender", "age", "life_stage_router", "diet", "free_text"];

  // The four phases shown by the progress bar. Branching only changes how many
  // steps live *inside* a phase, never the number of phases — so "Phase N of 4"
  // is always honest and the active fill is exact. Consent gates and the
  // loading/error steps aren't in any phase, so the bar hides itself there.
  var PHASES = [
    { label: "About you",      steps: ["gender", "age", "pregnancy", "menopause_periods", "menopause_symptoms"] },
    { label: "Your diet",      steps: ["diet", "diet_other", "fruit_veg", "dairy", "oily_fish"] },
    { label: "Your wellbeing", steps: ["health_goals", "symptoms", "nutrient_concerns"] },
    { label: "Finishing up",   steps: ["current_supplements", "free_text", "purchase_mode"] }
  ];
  var PHASE_OF = {};
  PHASES.forEach(function (ph, i) {
    ph.steps.forEach(function (s) { PHASE_OF[s] = i; });
  });

  // -----------------------------------------------------------------------
  // Init
  // -----------------------------------------------------------------------
  function init() {
    loadProducts();
    loadPromptConfig();
    bindQuizButtons();
    bindResultsInteractions();
    bindDisclaimer();
    bindEmailCapture();

    var token = new URLSearchParams(window.location.search).get("r");
    if (token) {
      rehydrateFromToken(token);
      return;
    }
    var cookieToken = getCookie("th_rec");
    if (cookieToken) {
      rehydrateFromToken(cookieToken);
      return;
    }
    showStep("health_confirmation");
  }

  // Restore a previously-saved submission so a customer arriving via the
  // PR_results_url email link sees their original recommendations without
  // re-taking the quiz. On any failure we silently fall back to a fresh quiz.
  function rehydrateFromToken(token) {
    if (!WORKER_URL) {
      showStep("health_confirmation");
      return;
    }
    var quizEl = document.getElementById("ai-quiz");
    if (quizEl) quizEl.style.display = "none";

    fetch(WORKER_URL.replace(/\/$/, "") + "/results/" + encodeURIComponent(token))
      .then(function (res) {
        // 410: the row is past its 30 days. 404: the token matches nothing,
        // which after the nightly purge rotates expired rows' tokens is the
        // same thing seen from a link that outlived its row. Either way the
        // customer needs the expired notice, not a silent fresh quiz.
        if (res.status === 410 || res.status === 404) {
          var expired = new Error("Results link expired");
          expired.expired = true;
          throw expired;
        }
        if (!res.ok) throw new Error("HTTP " + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!data || !data.recommendations) {
          throw new Error("Malformed results payload");
        }
        if (countResolvable(data.recommendations) === 0) {
          throw new Error("Saved recommendations contained no usable products");
        }
        // The worker returns no quiz answers for a saved submission — the link
        // is a key to the recommendations only — so the profile keeps its
        // defaults apart from the purchase preference the results screen reads.
        if (data.order_mode) profile.order_mode = data.order_mode;
        recommendations = data.recommendations;
        resultsToken = token;

        // Someone who already asked for these by email is not asked again.
        if (data.has_email) hideEmailCapture();

        renderResults(recommendations);
      })
      .catch(function (err) {
        if (err && err.expired) {
          var notice = document.getElementById("ai-results-expired");
          if (notice) notice.hidden = false;
        } else {
          console.error("AI Recommender: failed to rehydrate from token", err);
        }
        // Clear the dead token from the URL and the cookie so a refresh starts
        // clean instead of failing the same way again.
        if (window.history && window.history.replaceState) {
          window.history.replaceState({}, "", window.location.pathname);
        }
        deleteCookie("th_rec");
        if (quizEl) quizEl.style.display = "";
        showStep("health_confirmation");
      });
  }

  function loadProducts() {
    var script = document.getElementById("ai-recommender-products");
    if (script) {
      try {
        products = JSON.parse(script.textContent);
        // filter to available only
        products = products.filter(function (p) {
          return p.available;
        });
      } catch (e) {
        console.error("AI Recommender: could not parse product data", e);
      }
    }
  }

  function loadPromptConfig() {
    var script = document.getElementById("ai-recommender-prompt");
    if (script) {
      try {
        promptConfig = JSON.parse(script.textContent);
      } catch (e) {
        console.error("AI Recommender: could not parse prompt config", e);
      }
    }
  }

  // -----------------------------------------------------------------------
  // Quiz navigation
  // -----------------------------------------------------------------------
  function showWrapper() {
    // Scroll quiz container into view for initialisation
    var quizEl = document.getElementById("ai-quiz-container");
    if (quizEl) quizEl.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // Presentational only — these do not reflect real backend progress; they
  // exist so the ~30s+ wait reads as active work rather than a frozen spinner.
  // Two phases: a logical sequence played once (~40s at the 3.5s cadence
  // below), then an open-ended pool of non-sequential "almost there" lines
  // that cycles until results arrive. The pool is deliberately not a process
  // narrative so repeating it never looks like the work restarted.
  var LOADING_MESSAGES = [
    "Reviewing your profile and health goals…",
    "Checking your dietary preferences and restrictions…",
    "Noting any nutrient concerns you flagged…",
    "Scanning our supplement range…",
    "Reading the nutrition tables for each product…",
    "Matching nutrients to what your body needs…",
    "Factoring in your age and life stage…",
    "Screening out anything unsuitable for you…",
    "Comparing similar products side by side…",
    "Weighing up bundles for better value…",
    "Ranking the strongest matches for you…",
    "Personalising the reasons behind each pick…"
  ];
  var LOADING_HOLD_MESSAGES = [
    "Putting the final touches together…",
    "Almost there…",
    "Double-checking everything looks right…",
    "Just a few more moments…",
    "Tidying up your recommendations…",
    "Nearly ready…",
    "Hang tight, not long now…",
    "Thanks for your patience — almost done…"
  ];

  function loadingMessageAt(i) {
    if (i < LOADING_MESSAGES.length) return LOADING_MESSAGES[i];
    var h = (i - LOADING_MESSAGES.length) % LOADING_HOLD_MESSAGES.length;
    return LOADING_HOLD_MESSAGES[h];
  }

  function startLoadingMessages() {
    var el = document.getElementById("ai-quiz-loading-message");
    if (!el) return;
    stopLoadingMessages();
    var i = 0;
    el.style.opacity = "1";
    el.textContent = loadingMessageAt(0);
    loadingMessageTimer = window.setInterval(function () {
      i += 1;
      el.style.opacity = "0";
      window.setTimeout(function () {
        el.textContent = loadingMessageAt(i);
        el.style.opacity = "1";
      }, 300);
    }, 3500);
  }

  function stopLoadingMessages() {
    if (loadingMessageTimer) {
      window.clearInterval(loadingMessageTimer);
      loadingMessageTimer = null;
    }
  }

  // Walk the real flow from the first question, reusing resolveNextStep().
  // resolveNextStep() reads the live `profile`, so the path reflects the
  // user's actual answers so far.
  function buildQuizPath() {
    var s = "gender", path = [], guard = 0;
    while (s && s !== "submit" && guard++ < 40) {
      path.push(s);
      s = resolveNextStep(s);
    }
    return path;
  }

  // → { phaseIndex, fraction } for the current step, or null if the step
  // isn't part of any phase (consent gates, loading, error).
  function phaseProgress(step) {
    var idx = PHASE_OF[step];
    if (idx === undefined) return null;
    var path = buildQuizPath();
    var inPhase = path.filter(function (s) { return PHASE_OF[s] === idx; });

    // The menopause steps live in About-you but only appear once "Neither" is
    // answered on `pregnancy` — an answer not yet given while the bar is drawn
    // on the pregnancy step, so buildQuizPath() can't see them and About-you
    // would read near-complete. Count the pending steps here so the bar stays
    // honest. 45-54 always gets both; 36-44 is symptom-gated, so only Q2 is
    // guaranteed (Q1 appears only on Q2=Yes and self-corrects into the path
    // once answered).
    if (step === "pregnancy") {
      var pending = profile.age_range === "45-54"
        ? ["menopause_periods", "menopause_symptoms"]
        : profile.age_range === "36-44"
          ? ["menopause_symptoms"]
          : [];
      pending.forEach(function (s) {
        if (inPhase.indexOf(s) === -1) inPhase.push(s);
      });
    }

    var pos = inPhase.indexOf(step);                 // 0-based position in this phase
    if (pos === -1) { pos = 0; }
    var fraction = (pos + 1) / inPhase.length;        // current step counts as in-progress
    return { phaseIndex: idx, fraction: fraction };
  }

  function renderPhaseBar(step) {
    var host = document.getElementById("ai-quiz-phasebar");
    if (!host) return;
    var p = phaseProgress(step);
    if (!p) { host.hidden = true; host.innerHTML = ""; return; }
    host.hidden = false;

    var segs = PHASES.map(function (ph, k) {
      var w = k < p.phaseIndex ? 100 : (k === p.phaseIndex ? Math.round(p.fraction * 100) : 0);
      var cls = k < p.phaseIndex ? " is-done" : "";
      return '<span class="ai-phasebar__seg' + cls + '"><i style="width:' + w + '%"></i></span>';
    }).join("");

    var labels = PHASES.map(function (ph, k) {
      var cls = k < p.phaseIndex ? "is-done" : (k === p.phaseIndex ? "is-active" : "");
      return '<span class="' + cls + '">' + ph.label + '</span>';
    }).join("");

    host.innerHTML =
      '<p class="ai-phasebar__caption">Phase <b>' + (p.phaseIndex + 1) +
        '</b> of 4 · ' + PHASES[p.phaseIndex].label + '</p>' +
      '<div class="ai-phasebar__track">' + segs + '</div>' +
      '<div class="ai-phasebar__labels">' + labels + '</div>';
  }

  function showStep(stepName) {
    // Hide all steps
    var steps = document.querySelectorAll(".ai-quiz__step");
    for (var i = 0; i < steps.length; i++) {
      steps[i].classList.add("ai-quiz__step--hidden");
    }

    // Show target step
    var target = document.querySelector('[data-step="' + stepName + '"]');
    if (target) target.classList.remove("ai-quiz__step--hidden");

    // Update the phase progress bar (hides itself on consent/loading/error)
    renderPhaseBar(stepName);

    if (stepName === "loading") {
      startLoadingMessages();
    } else {
      stopLoadingMessages();
    }

    // Back button
    var backEl = document.getElementById("ai-quiz-back");
    if (stepHistory.length > 0 && stepName !== "loading") {
      backEl.classList.remove("ai-quiz__back--hidden");
    } else {
      backEl.classList.add("ai-quiz__back--hidden");
    }

    // Scroll quiz into view
    if(stepName=="health_confirmation") {
      showWrapper();
    } else {
      var quizEl = document.getElementById("ai-quiz");
      if (quizEl) {
        quizEl.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }

  function nextStep(currentStep) {
    stepHistory.push(currentStep);
    var next = resolveNextStep(currentStep);
    if (next === "submit") {
      submitQuiz();
    } else {
      showStep(next);
    }
  }

  function resolveNextStep(current) {
    switch (current) {
      case "health_confirmation":
        // Anyone on medication or with a condition is routed to an interstitial
        // advising them to consult a healthcare professional first; they can
        // still opt to continue from there.
        if (profile.health_confirmed === "yes") return "terms_acceptance";
        return "health_blocked";

      case "health_blocked":
        return "terms_acceptance";

      case "terms_acceptance":
        return "gender";

      case "gender":
        return "age";

      case "age":
        // Determine life-stage question based on gender + age
        if (profile.gender === "female") {
          // At 55+ "periods stopped" is effectively always yes, so skip Q1 and
          // ask only the symptom question; resolved as postmenopausal at submit.
          if (profile.age_range === "55+") {
            return "menopause_symptoms";
          } else {
            return "pregnancy";
          }
        }
        // Males skip life stage
        return "diet";

      case "pregnancy":
        // Only "Neither" continues to the menopause questions. The entry point
        // differs by age band (see step 3b comment in the section markup):
        if (profile.life_stage === "neither") {
          // 45-54: ask both, Q1 (periods) first.
          if (profile.age_range === "45-54") return "menopause_periods";
          // 36-44: symptom-gated, Q2 (symptoms) first.
          if (profile.age_range === "36-44") return "menopause_symptoms";
        }
        return "diet";

      case "menopause_periods":
        // 45-54 reaches Q1 first and still has Q2 to answer. 36-44 only reaches
        // Q1 after answering Q2=Yes, so it's the last menopause step for them.
        if (profile.age_range === "45-54") return "menopause_symptoms";
        return "diet";

      case "menopause_symptoms":
        // 36-44 is symptom-gated: only ask Q1 (periods) if symptoms are present.
        if (profile.age_range === "36-44" && profile.menopause_symptoms_now === "yes") {
          return "menopause_periods";
        }
        return "diet";

      case "diet":
        // Always ask for any other dietary preferences / exclusions next
        return "diet_other";

      case "diet_other":
        // Vegans skip the food-detail questions
        if (profile.diet === "vegan") {
          return "health_goals";
        }
        // Vegetarian and none both get fruit & veg → dairy
        return "fruit_veg";

      case "fruit_veg":
        return "dairy";

      case "dairy":
        // Only non-vegetarians get the oily fish question
        if (profile.diet === "none") {
          return "oily_fish";
        }
        return "health_goals";

      case "oily_fish":
        return "health_goals";

      case "health_goals":
        return "symptoms";

      case "symptoms":
        return "nutrient_concerns";

      case "nutrient_concerns":
        return "current_supplements";

      case "current_supplements":
        return "free_text";

      case "free_text":
        return "purchase_mode";

      case "purchase_mode":
        return "submit";

      default:
        return "submit";
    }
  }

  function goBack() {
    if (stepHistory.length === 0) return;
    var prev = stepHistory.pop();

    // Clear the field that was set in the previous step
    switch (prev) {
      case "health_confirmation":
        profile.health_confirmed = null;
        break;
      case "gender":
        profile.gender = null;
        break;
      case "age":
        profile.age_range = null;
        break;
      case "pregnancy":
        profile.life_stage = null;
        break;
      case "menopause_periods":
        profile.menopause_periods_stopped = null;
        break;
      case "menopause_symptoms":
        profile.menopause_symptoms_now = null;
        break;
      case "diet":
        profile.diet = null;
        break;
      case "diet_other":
        profile.diet_other = "";
        var dietOtherTa = document.getElementById("ai-quiz-diet-other");
        if (dietOtherTa) dietOtherTa.value = "";
        break;
      case "fruit_veg":
        profile.fruit_veg_portions = null;
        break;
      case "dairy":
        profile.eats_dairy = null;
        break;
      case "oily_fish":
        profile.oily_fish_per_week = null;
        break;
      case "health_goals":
        profile.health_goals = [];
        var goalBtns = document.querySelectorAll(".ai-quiz__btn--goal");
        for (var g = 0; g < goalBtns.length; g++) {
          goalBtns[g].classList.remove("ai-quiz__btn--goal-selected");
        }
        break;
      case "symptoms":
        profile.symptoms = [];
        var symptomBtns = document.querySelectorAll(".ai-quiz__btn--symptom");
        for (var s = 0; s < symptomBtns.length; s++) {
          symptomBtns[s].classList.remove("ai-quiz__btn--goal-selected");
        }
        break;
      case "nutrient_concerns":
        profile.nutrient_concerns = [];
        var nutBtns = document.querySelectorAll(".ai-quiz__btn--nutrient");
        for (var n = 0; n < nutBtns.length; n++) {
          nutBtns[n].classList.remove("ai-quiz__btn--goal-selected");
        }
        break;
      case "current_supplements":
        profile.current_supplements = "";
        break;
      case "purchase_mode":
        profile.order_mode = "subscription";
        break;
    }

    showStep(prev);
  }

  // -----------------------------------------------------------------------
  // Quiz button handlers
  // -----------------------------------------------------------------------
  function bindQuizButtons() {
    // Option buttons (gender, age, life stage, diet)
    var btns = document.querySelectorAll(".ai-quiz__btn[data-field]");
    for (var i = 0; i < btns.length; i++) {
      btns[i].addEventListener("click", handleOptionClick);
    }

    // Health goals — multi-select toggle
    var goalBtns = document.querySelectorAll(".ai-quiz__btn--goal");
    for (var g = 0; g < goalBtns.length; g++) {
      goalBtns[g].addEventListener("click", function () {
        var goal = this.getAttribute("data-goal");
        var idx = profile.health_goals.indexOf(goal);
        if (idx > -1) {
          profile.health_goals.splice(idx, 1);
          this.classList.remove("ai-quiz__btn--goal-selected");
        } else {
          profile.health_goals.push(goal);
          this.classList.add("ai-quiz__btn--goal-selected");
        }
        var goalsNextBtn = document.getElementById("ai-quiz-goals-next");
        if (goalsNextBtn) goalsNextBtn.textContent = profile.health_goals.length > 0 ? "Next" : "Skip";
      });
    }

    // Health goals — next/skip (unified)
    var goalsNext = document.getElementById("ai-quiz-goals-next");
    if (goalsNext)
      goalsNext.addEventListener("click", function () {
        nextStep("health_goals");
      });

    // Nutrient concerns — multi-select toggle
    var nutBtns = document.querySelectorAll(".ai-quiz__btn--nutrient");
    for (var n = 0; n < nutBtns.length; n++) {
      nutBtns[n].addEventListener("click", function () {
        var nutrient = this.getAttribute("data-nutrient");
        var idx = profile.nutrient_concerns.indexOf(nutrient);
        if (idx > -1) {
          profile.nutrient_concerns.splice(idx, 1);
          this.classList.remove("ai-quiz__btn--goal-selected");
        } else {
          profile.nutrient_concerns.push(nutrient);
          this.classList.add("ai-quiz__btn--goal-selected");
        }
        var nutrientsNextBtn = document.getElementById("ai-quiz-nutrients-next");
        if (nutrientsNextBtn) nutrientsNextBtn.textContent = profile.nutrient_concerns.length > 0 ? "Next" : "Skip";
      });
    }

    // Nutrient concerns — next/skip (unified)
    var nutrientsNext = document.getElementById("ai-quiz-nutrients-next");
    if (nutrientsNext)
      nutrientsNext.addEventListener("click", function () {
        nextStep("nutrient_concerns");
      });

    // Symptoms — multi-select toggle
    var symptomBtns = document.querySelectorAll(".ai-quiz__btn--symptom");
    for (var sy = 0; sy < symptomBtns.length; sy++) {
      symptomBtns[sy].addEventListener("click", function () {
        var symptom = this.getAttribute("data-symptom");
        var idx = profile.symptoms.indexOf(symptom);
        if (idx > -1) {
          profile.symptoms.splice(idx, 1);
          this.classList.remove("ai-quiz__btn--goal-selected");
        } else {
          profile.symptoms.push(symptom);
          this.classList.add("ai-quiz__btn--goal-selected");
        }
        var symptomsNextBtn = document.getElementById("ai-quiz-symptoms-next");
        if (symptomsNextBtn) symptomsNextBtn.textContent = profile.symptoms.length > 0 ? "Next" : "Skip";
      });
    }

    // Symptoms — next/skip (unified)
    var symptomsNext = document.getElementById("ai-quiz-symptoms-next");
    if (symptomsNext)
      symptomsNext.addEventListener("click", function () {
        nextStep("symptoms");
      });

    // Diet other — next/skip (unified)
    var dietOtherNext = document.getElementById("ai-quiz-diet-other-next");
    if (dietOtherNext) {
      var dietOtherTa = document.getElementById("ai-quiz-diet-other");
      if (dietOtherTa) {
        dietOtherTa.addEventListener("input", function () {
          dietOtherNext.textContent = dietOtherTa.value.trim() ? "Next" : "Skip";
        });
      }
      dietOtherNext.addEventListener("click", function () {
        profile.diet_other = dietOtherTa ? dietOtherTa.value.trim() : "";
        nextStep("diet_other");
      });
    }

    // Current supplements — next/skip (unified)
    var suppsNext = document.getElementById("ai-quiz-supps-next");
    if (suppsNext) {
      var suppsTa = document.getElementById("ai-quiz-current-supps");
      if (suppsTa) {
        suppsTa.addEventListener("input", function () {
          suppsNext.textContent = suppsTa.value.trim() ? "Next" : "Skip";
        });
      }
      suppsNext.addEventListener("click", function () {
        profile.current_supplements = suppsTa ? suppsTa.value : "";
        nextStep("current_supplements");
      });
    }

    // Free text — submit/skip (unified)
    var freeNext = document.getElementById("ai-quiz-freetext-next");
    if (freeNext) {
      var freeTa = document.getElementById("ai-quiz-freetext");
      if (freeTa) {
        freeTa.addEventListener("input", function () {
          freeNext.textContent = freeTa.value.trim() ? "Get my recommendations" : "Skip";
        });
      }
      freeNext.addEventListener("click", function () {
        profile.free_text = freeTa ? freeTa.value : "";
        nextStep("free_text");
      });
    }

    // Purchase mode — card selection
    var purchaseCards = document.querySelectorAll('.ai-quiz__purchase-card');
    for (var p = 0; p < purchaseCards.length; p++) {
      purchaseCards[p].addEventListener("click", function (e) {
        e.preventDefault();
        var card = e.currentTarget;
        profile.order_mode = card.getAttribute("data-value");
        for (var c = 0; c < purchaseCards.length; c++) {
          purchaseCards[c].classList.toggle(
            "ai-quiz__purchase-card--selected",
            purchaseCards[c] === card
          );
        }
      });
    }
    var purchaseContinueBtn = document.getElementById("ai-quiz-purchase-continue");
    if (purchaseContinueBtn) {
      purchaseContinueBtn.addEventListener("click", function () {
        if (!profile.order_mode) profile.order_mode = "subscription";
        nextStep("purchase_mode");
      });
    }

    // Back
    var backLink = document.querySelector("#ai-quiz-back a");
    if (backLink)
      backLink.addEventListener("click", function (e) {
        e.preventDefault();
        goBack();
      });

    // Health-blocked screen — user chooses to continue regardless
    var healthContinueBtn = document.getElementById("ai-quiz-health-continue");
    if (healthContinueBtn)
      healthContinueBtn.addEventListener("click", function () {
        nextStep("health_blocked");
      });

    // Terms acceptance — enable/disable Continue as checkbox changes
    var termsCb = document.getElementById("ai-terms-checkbox");
    var termsContinueBtn = document.getElementById("ai-terms-continue");
    if (termsCb && termsContinueBtn) {
      termsCb.addEventListener("change", function () {
        termsContinueBtn.disabled = !termsCb.checked;
      });
      var termsErrorEl = document.getElementById("ai-terms-error");
      termsContinueBtn.addEventListener("click", function () {
        if (!termsCb.checked) return;
        if (termsErrorEl) {
          termsErrorEl.hidden = true;
          termsErrorEl.textContent = "";
        }
        termsContinueBtn.disabled = true;
        // Record consent before advancing so the quiz never runs without a
        // consent_id; surface any failure right here so the customer can retry.
        // submitQuiz also awaits recordConsent() as a backstop (cached id).
        recordConsent()
          .then(function () {
            nextStep("terms_acceptance");
          })
          .catch(function (err) {
            console.error("AI Recommender: consent request failed", err);
            termsContinueBtn.disabled = false;
            if (termsErrorEl) {
              termsErrorEl.textContent =
                "We couldn't record your consent — please check your connection and try again.";
              termsErrorEl.hidden = false;
            }
          });
      });
    }

    // Retry on error
    var retryBtn = document.getElementById("ai-quiz-retry");
    if (retryBtn) retryBtn.addEventListener("click", submitQuiz);

    // Restart
    var restartBtn = document.getElementById("ai-restart");
    if (restartBtn)
      restartBtn.addEventListener("click", function (e) {
        e.preventDefault();
        resetAll();
      });
  }

  function handleOptionClick(e) {
    var btn = e.currentTarget;
    var field = btn.getAttribute("data-field");
    var value = btn.getAttribute("data-value");
    profile[field] = value;

    // Determine which step we're on from the parent
    var stepEl = btn.closest(".ai-quiz__step");
    var stepName = stepEl ? stepEl.getAttribute("data-step") : field;
    // First real interaction (the health-confirmation answer) — open the
    // anonymous funnel row so consent / quiz drop-off becomes measurable.
    if (stepName === "health_confirmation") startFunnel();
    nextStep(stepName);
  }

  // Create the anonymous funnel row at quiz start, before consent. Sends nothing
  // personal — just gets a token we carry through the rest of the flow so every
  // abandon point is measurable. Fires once; the token stays in memory (no
  // cookie) until after consent.
  function startFunnel() {
    if (resultsToken || !WORKER_URL) return;
    fetch(WORKER_URL.replace(/\/$/, "") + "/start", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ source: sessionSource }),
    })
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (data) {
        if (data && data.token) resultsToken = data.token;
      })
      .catch(function (err) {
        console.error("AI Recommender: failed to start funnel", err);
      });
  }

  // -----------------------------------------------------------------------
  // GDPR consent — the worker requires a recorded consent_id before it will
  // generate or persist any recommendation. We record consent when the user
  // accepts the terms step, hash the exact disclosure they saw for the audit
  // trail, and reuse the resulting id for the AI call.
  // -----------------------------------------------------------------------
  function sha256Hex(text) {
    var bytes = new TextEncoder().encode(text);
    return crypto.subtle.digest("SHA-256", bytes).then(function (buf) {
      var view = new Uint8Array(buf);
      var hex = "";
      for (var i = 0; i < view.length; i++) {
        hex += (view[i] < 16 ? "0" : "") + view[i].toString(16);
      }
      return hex;
    });
  }

  // The checkbox label is the operative agreement; statement_text records it.
  function getConsentStatement() {
    var label = document.querySelector(".ai-quiz__terms-agree span");
    var text = label ? label.textContent.replace(/\s+/g, " ").trim() : "";
    return (
      text ||
      "I agree to my answers being used to generate my recommendations. I have read the Privacy Policy."
    );
  }

  // The full terms step is the disclosure; its hash versions what was shown.
  function getConsentDisclosure() {
    var step = document.querySelector('[data-step="terms_acceptance"]');
    var text = step ? step.textContent.replace(/\s+/g, " ").trim() : "";
    return text || getConsentStatement();
  }

  // Server-side SHA-256 of the live Privacy Policy page content, rendered into
  // the page by Liquid. Lets the audit hash version the actual policy document,
  // not just the on-screen wording. Empty if the element is missing/invalid.
  function getPolicyContentHash() {
    var el = document.getElementById("ai-recommender-policy-hash");
    var hash = el ? el.textContent.trim() : "";
    return /^[0-9a-f]{64}$/.test(hash) ? hash : "";
  }

  // Records consent once and caches the id. Returns a promise resolving to the
  // consent_id; reuses any in-flight request so the AI call never races ahead.
  // policy_sha256 hashes BOTH the on-screen disclosure and a fingerprint of the
  // Privacy Policy content, so a change to either invalidates the audit hash.
  function recordConsent() {
    if (consentId) return Promise.resolve(consentId);
    if (consentPromise) return consentPromise;
    if (!WORKER_URL) return Promise.reject(new Error("No worker URL configured"));

    var policyContentHash = getPolicyContentHash();
    if (!policyContentHash) {
      return Promise.reject(new Error("Missing or invalid privacy policy hash"));
    }

    var statementText = getConsentStatement();
    var consentedAt = new Date().toISOString();
    var auditInput =
      getConsentDisclosure() + "\n—policy-content-sha256:" + policyContentHash;

    consentPromise = sha256Hex(auditInput)
      .then(function (policySha256) {
        return fetch(WORKER_URL.replace(/\/$/, "") + "/consent", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            consented_at: consentedAt,
            statement_text: statementText,
            policy_sha256: policySha256,
            // Links consent to the funnel row from /start and advances it to
            // 'consented' so quiz drop-off is distinguishable from consent
            // drop-off. Null if /start hadn't returned yet — non-fatal.
            submission_token: resultsToken,
          }),
        });
      })
      .then(function (res) {
        if (!res.ok) throw new Error("HTTP " + res.status);
        return res.json();
      })
      .then(function (data) {
        if (!data || !data.consent_id) throw new Error("No consent_id returned");
        consentId = data.consent_id;
        return consentId;
      })
      .catch(function (err) {
        consentPromise = null; // let a later attempt (e.g. retry) try again
        throw err;
      });

    return consentPromise;
  }

  // -----------------------------------------------------------------------
  // Submit to AI
  // -----------------------------------------------------------------------
  // Collapse the two raw menopause answers into the internal stage value plus
  // the two flags the recommender routes on. Runs at submit so the derived
  // fields travel with the profile to the worker (and into the persisted row).
  // Gated on menopause_symptoms_now — the one answer set on every menopause
  // branch (36-44, 45-54 and 55+) — so males, 16-35 women and pregnant/
  // trying/breastfeeding women get nothing attached.
  function deriveMenopauseProfile() {
    if (profile.menopause_symptoms_now === null) return;

    var hasSymptoms = profile.menopause_symptoms_now === "yes";
    // Q1 is skipped at 55+ (periods effectively always stopped — treat as yes)
    // and for the 36-44 Q2=No exit (never asked — stays null, falling through to
    // premenopausal below, which is the intended assumption at that age).
    var periodsStopped =
      profile.age_range === "55+" ? "yes" : profile.menopause_periods_stopped;

    var stage;
    if (periodsStopped === "yes") {
      stage = hasSymptoms ? "postmenopausal_symptomatic" : "postmenopausal_settled";
    } else if (periodsStopped === "not_sure") {
      stage = hasSymptoms ? "symptomatic_unspecified" : "ambiguous";
    } else {
      stage = hasSymptoms ? "perimenopausal" : "premenopausal";
    }

    profile.menopause_stage = stage;
    profile.has_menopause_symptoms = hasSymptoms;
    profile.is_postmenopausal = periodsStopped === "yes";
  }

  function submitQuiz() {
    showStep("loading");
    deriveMenopauseProfile();

    // Build a product list for the AI — strip cart/display fields, keep everything
    // Claude needs to make informed recommendations.
    var aiProducts = products.map(function (p) {
      var slim = {
        handle: p.handle,
        title: p.title,
        productType: p.productType,
        price: p.price,
      };

      // Supplement fields
      if (p.capsules) slim.capsules = p.capsules;
      if (p.dose) slim.dose = p.dose;
      if (p["ingredients metaobject"]) slim["ingredients metaobject"] = p["ingredients metaobject"];
      if (p["nutrition table"]) slim["nutrition table"] = p["nutrition table"];
      if (p["key highlights metaobject"]) slim["key highlights metaobject"] = p["key highlights metaobject"];
      if (p["suggested use"]) slim["suggested use"] = p["suggested use"];
      if (p["product warnings"]) slim["product warnings"] = p["product warnings"];

      // Bundle fields
      if (p.products) slim.products = p.products;

      return slim;
    });

    var url = WORKER_URL;
    if (!url) {
      console.error("AI Recommender: No worker URL configured");
      showStep("error");
      return;
    }

    recordConsent()
      .then(function (consent_id) {
        return fetch(url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            customer_profile: profile,
            products: aiProducts,
            prompt_config: promptConfig || null,
            consent_id: consent_id,
            // The funnel row created at /start; the worker attaches the answers
            // to it. Null falls back to a fresh insert worker-side.
            token: resultsToken,
            source: sessionSource,
          }),
        });
      })
      .then(function (res) {
        if (!res.ok) throw new Error("HTTP " + res.status);
        // The worker attaches the answers to the funnel row and echoes its token
        // here (the same one from /start, or a fresh one if /start failed).
        var token = res.headers.get("X-Recommender-Token");
        if (token) {
          resultsToken = token;
          setCookie("th_rec", token, 30);
          updateSupportReference();
        }
        return res.json();
      })
      .then(function (data) {
        if (data.error) throw new Error(data.error);
        if (countResolvable(data) === 0) {
          throw new Error("Recommendation response contained no usable products");
        }
        recommendations = data;
        showDisclaimer();
      })
      .catch(function (err) {
        console.error("AI Recommender error:", err);
        showStep("error");
      });
  }

  function buildResultsUrl(token) {
    return window.location.origin + "/pages/ai-recommender?r=" + encodeURIComponent(token);
  }

  // Tell the worker the customer actually saw their results. The token now
  // arrives on the AI-call response (X-Recommender-Token), so it's set before
  // the disclaimer; this still guards on resultsRendered so we only fire once
  // results are on screen. keepalive lets the request survive navigation.
  function markResultsViewed() {
    if (resultsViewedSent || !resultsRendered || !WORKER_URL || !resultsToken) return;
    resultsViewedSent = true;
    fetch(WORKER_URL.replace(/\/$/, "") + "/viewed", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ token: resultsToken }),
      keepalive: true,
    }).catch(function (err) {
      resultsViewedSent = false; // allow a retry on the next trigger
      console.error("AI Recommender: failed to mark results viewed", err);
    });
  }

  // -----------------------------------------------------------------------
  // Disclaimer gate — shown after the worker response, before results render
  // -----------------------------------------------------------------------
  function showDisclaimer() {
    // Success path reaches here without another showStep() call, so the
    // loading rotator must be stopped explicitly.
    stopLoadingMessages();

    // Hide the quiz UI; keep results hidden until the user agrees
    var quizEl = document.getElementById("ai-quiz");
    var quizHeader = document.getElementById("ai-quiz-header");
    if (quizEl) quizEl.style.display = "none";
    if (quizHeader) quizHeader.style.display = "none";

    var disclaimerEl = document.getElementById("ai-disclaimer");
    if (disclaimerEl) {
      disclaimerEl.classList.remove("ai-recommender__disclaimer--hidden");
      var rect = disclaimerEl.getBoundingClientRect();
      if (rect.top < 0 || rect.top > window.innerHeight * 0.5) {
        disclaimerEl.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }

  function bindDisclaimer() {
    var cb = document.getElementById("ai-disclaimer-checkbox");
    var btn = document.getElementById("ai-disclaimer-continue");
    if (!cb || !btn) return;

    cb.addEventListener("change", function () {
      btn.disabled = !cb.checked;
    });

    btn.addEventListener("click", function () {
      if (!cb.checked || !recommendations) return;
      var disclaimerEl = document.getElementById("ai-disclaimer");
      if (disclaimerEl) disclaimerEl.classList.add("ai-recommender__disclaimer--hidden");
      renderResults(recommendations);
    });
  }

  // -----------------------------------------------------------------------
  // Render results
  // -----------------------------------------------------------------------
  function renderResults(data) {
    selectedItems = [];

    // Hide quiz, show results
    document.getElementById("ai-quiz").style.display = "none";
    document.getElementById("ai-quiz-header").style.display = "none";
    var resultsEl = document.getElementById("ai-results");
    resultsEl.classList.remove("ai-recommender__results--hidden");
    if (emailSkipped()) hideEmailCapture();

    // Pre-select purchase mode from quiz answer
    var modeValue = profile.order_mode || "subscription";
    var modeRadio = document.querySelector('input[name="ai_order_mode"][value="' + modeValue + '"]');
    if (modeRadio) modeRadio.checked = true;

    // Render highly recommended
    var highlyList = document.getElementById("ai-results-highly-list");
    highlyList.innerHTML = "";
    if (data.highly_recommended && data.highly_recommended.length > 0) {
      for (var i = 0; i < data.highly_recommended.length; i++) {
        var rec = data.highly_recommended[i];
        var product = findProduct(rec.handle);
        if (product) {
          highlyList.appendChild(buildProductCard(product, rec.reason, true));
          selectedItems.push(product.handle); // auto-select highly recommended
        }
      }
      document.getElementById("ai-results-highly").style.display = "";
    } else {
      document.getElementById("ai-results-highly").style.display = "none";
    }

    // Render also consider
    var alsoList = document.getElementById("ai-results-also-list");
    alsoList.innerHTML = "";
    if (data.also_consider && data.also_consider.length > 0) {
      for (var j = 0; j < data.also_consider.length; j++) {
        var rec2 = data.also_consider[j];
        var product2 = findProduct(rec2.handle);
        if (product2) {
          alsoList.appendChild(buildProductCard(product2, rec2.reason, false));
        }
      }
      document.getElementById("ai-results-also").style.display = "";
    } else {
      document.getElementById("ai-results-also").style.display = "none";
    }

    // Render Together Health alternatives (Together versions of other-brand supplements
    // the customer already takes). Unselected by default — customer opts in per-card.
    var altsList = document.getElementById("ai-results-alternatives-list");
    altsList.innerHTML = "";
    if (data.together_alternatives && data.together_alternatives.length > 0) {
      for (var k = 0; k < data.together_alternatives.length; k++) {
        var rec3 = data.together_alternatives[k];
        var product3 = findProduct(rec3.handle);
        if (product3) {
          altsList.appendChild(buildProductCard(product3, rec3.reason, false));
        }
      }
      document.getElementById("ai-results-alternatives").style.display = "";
    } else {
      document.getElementById("ai-results-alternatives").style.display = "none";
    }

    updateCart();
    updateSupportReference();
    resultsRendered = true;
    markResultsViewed();
    scrollToResults();
  }

  // Populate the "Quote this ID to customer service" box on the results screen.
  // resultsToken is set when loading shared results and from the AI-call
  // response header — so this is called from both paths.
  function updateSupportReference() {
    var box = document.getElementById("ai-results-support");
    var idEl = document.getElementById("ai-results-support-id");
    if (!box || !idEl) return;
    if (!resultsToken) {
      box.hidden = true;
      return;
    }
    idEl.textContent = resultsToken;
    box.hidden = false;
  }

  // Replace [n] markers with <sup>n</sup> footnote tags (applied after escapeHtml)
  function sup(str) {
    return str.replace(/\[(\d+)\]/g, "<sup>$1</sup>");
  }

  // Convert a Shopify rich text JSON string to safe HTML.
  // Falls back to escapeHtml(str) for plain text fields.
  function parseRichText(str) {
    if (!str || typeof str !== "string") return "";
    var trimmed = str.trim();
    if (trimmed.charAt(0) !== "{") return escapeHtml(str);
    try {
      var doc = JSON.parse(trimmed);
      if (doc && doc.type === "root" && Array.isArray(doc.children)) {
        return richNodesToHtml(doc.children);
      }
    } catch (e) {}
    return escapeHtml(str);
  }

  function richNodesToHtml(nodes) {
    var out = "";
    for (var i = 0; i < nodes.length; i++) {
      var n = nodes[i];
      switch (n.type) {
        case "paragraph":
          out += "<p>" + richNodesToHtml(n.children || []) + "</p>";
          break;
        case "heading":
          out += "<strong>" + richNodesToHtml(n.children || []) + "</strong>";
          break;
        case "list":
          var tag = n.listType === "ordered" ? "ol" : "ul";
          out += "<" + tag + ">" + richNodesToHtml(n.children || []) + "</" + tag + ">";
          break;
        case "list-item":
          out += "<li>" + richNodesToHtml(n.children || []) + "</li>";
          break;
        case "link":
          out += richNodesToHtml(n.children || []);
          break;
        case "text": {
          var txt = escapeHtml(n.value || "");
          if (n.bold) txt = "<strong>" + txt + "</strong>";
          if (n.italic) txt = "<em>" + txt + "</em>";
          out += txt;
          break;
        }
        default:
          if (n.children) out += richNodesToHtml(n.children);
      }
    }
    return out;
  }

  function handleise(string) {
    return string.replace(/[^a-zA-Z0-9 ]+/g,"").replace(/ /g,"-").toLowerCase();
  }

  // Build a single collapsible <details> panel for the product card
  function buildDetailPanel(title, bodyHtml) {
    return (
      '<details class="ai-panel" data-content="' + handleise(title) + '">' +
        '<summary class="ai-panel__summary">' + title + '</summary>' +
        '<div class="ai-panel__body">' + bodyHtml + '</div>' +
      '</details>'
    );
  }

  function buildItemPriceHtml(product, isSubscription) {
    var priceInPounds = product.price_raw ? (product.price_raw / 100) : 0;
    var oneTime = "£" + priceInPounds.toFixed(2);
    var hasDiscount = product.discount_percentage && product.discount_percentage > 0;

    if (!hasDiscount) {
      return oneTime +
        '<span class="ai-results__item-per-day">£' + (priceInPounds / 30).toFixed(2) + '/day</span>';
    }

    var discounted = priceInPounds * (100 - product.discount_percentage) / 100;
    var subPrice = "£" + discounted.toFixed(2);

    if (isSubscription) {
      return '<span class="ai-results__item-strike-price">' + oneTime + '</span> ' +
        '<span class="ai-results__item-active-price">' + subPrice + ' on subscription</span>' +
        '<span class="ai-results__item-per-day">£' + (discounted / 30).toFixed(2) + '/day on subscription</span>';
    }
    return oneTime +
      ' <span class="ai-results__item-sub-price">(' + subPrice + ' on subscription)</span>' +
      '<span class="ai-results__item-per-day">£' + (priceInPounds / 30).toFixed(2) + '/day</span>';
  }

  function buildCartItemPriceHtml(product, isSubscription) {
    var priceInPounds = product.price_raw ? (product.price_raw / 100) : 0;
    var oneTime = "£" + priceInPounds.toFixed(2);
    var hasDiscount = product.discount_percentage && product.discount_percentage > 0;
    if (!isSubscription || !hasDiscount) return oneTime;
    var discounted = priceInPounds * (100 - product.discount_percentage) / 100;
    return '<span class="ai-results__cart-item-strike">' + oneTime + '</span> £' + discounted.toFixed(2);
  }

  function refreshItemPriceDisplays() {
    var isSubscription = getOrderMode() === "subscription";
    var priceEls = document.querySelectorAll('.ai-results__item[data-handle]');
    for (var i = 0; i < priceEls.length; i++) {
      var handle = priceEls[i].getAttribute("data-handle");
      var product = findProduct(handle);
      if (!product) continue;
      var priceEl = priceEls[i].querySelector(".ai-results__item-price");
      if (priceEl) priceEl.innerHTML = buildItemPriceHtml(product, isSubscription);
    }
  }

  function buildProductCard(product, reason, autoSelected) {
    var li = document.createElement("li");
    li.className = "ai-results__item" + (autoSelected ? " ai-results__item--selected" : "");
    li.setAttribute("data-handle", product.handle);

    // Pricing — price_raw is in pence (Shopify money format), convert to pounds
    var priceHtml = buildItemPriceHtml(product, getOrderMode() === "subscription");

    // Highlights — pull from the metaobject structure
    var highlights = product["key highlights metaobject"] && product["key highlights metaobject"].list
      ? product["key highlights metaobject"].list
      : [];
    var highlightsHtml = "";
    if (highlights.length > 0) {
      highlightsHtml = '<ul class="ai-results__item-highlights">';
      var max = Math.min(highlights.length, 3);
      for (var h = 0; h < max; h++) {
        highlightsHtml += "<li>" + escapeHtml(highlights[h]) + "</li>";
      }
      highlightsHtml += "</ul>";
    }

    // Allergens — display-only, straight from the custom.allergens metaobject list
    var allergensHtml = "";
    if (product.allergens && product.allergens.length > 0) {
      var allergenLabels = [];
      for (var ai = 0; ai < product.allergens.length; ai++) {
        allergenLabels.push(escapeHtml(product.allergens[ai]));
      }
      allergensHtml =
        '<p class="ai-results__item-allergens">' +
          '<span class="ai-results__item-allergens-label">Allergens:</span> ' +
          allergenLabels.join(", ") +
        "</p>";
    }

    // ---- Expandable detail panels ----

    // Ingredient label
    var ingredientPanelHtml = "";
    var ingredientsMo = product["ingredients metaobject"];
    if (ingredientsMo && ingredientsMo.text && ingredientsMo.text.trim()) {
      ingredientPanelHtml = buildDetailPanel(
        "Ingredients",
        '<div class="ai-panel__ingredients">' + parseRichText(ingredientsMo.text.trim()) + '</div>'
      );
    }

    // Nutrition table
    var nutritionPanelHtml = "";
    var nutritionData = product["nutrition table"];
    if (nutritionData && nutritionData.nutrition_lines && nutritionData.nutrition_lines.length > 0) {
      var nutBodyHtml = "";
      if (nutritionData.intro_text) {
        nutBodyHtml += '<p class="ai-panel__nutrition-intro">' + sup(escapeHtml(nutritionData.intro_text)) + '</p>';
      }
      nutBodyHtml += '<table class="ai-panel__nutrition"><thead><tr><th>Nutrient</th><th>Amount</th><th>NRV</th></tr></thead><tbody>';
      for (var ni = 0; ni < nutritionData.nutrition_lines.length; ni++) {
        var line = nutritionData.nutrition_lines[ni];
        nutBodyHtml +=
          '<tr>' +
            '<td>' + sup(escapeHtml(line.nutrient || "")) + '</td>' +
            '<td>' + escapeHtml(line.amount || "") + '</td>' +
            '<td>' + escapeHtml(line.nrv || "") + '</td>' +
          '</tr>';
      }
      nutBodyHtml += '</tbody></table>';
      if (nutritionData.footer_text) {
        nutBodyHtml += '<p class="ai-panel__nutrition-footer">' + sup(escapeHtml(nutritionData.footer_text)) + '</p>';
      }
      nutritionPanelHtml = buildDetailPanel("Nutrition", nutBodyHtml);
    }

    // Suggested use
    var suggestedUsePanelHtml = "";
    var su = product["suggested use"];
    if (su) {
      var suBodyHtml = "";
      if (su.label) {
        suBodyHtml += '<p class="ai-panel__su-label">' + escapeHtml(su.label) + '</p>';
      }
      if (su.usages && su.usages.length > 0) {
        suBodyHtml += '<ul class="ai-panel__su-list">';
        for (var ui = 0; ui < su.usages.length; ui++) {
          var usage = su.usages[ui];
          suBodyHtml += '<li class="ai-panel__su-usage"><strong>' + escapeHtml(usage.title || "") + '</strong>';
          if (usage.content) suBodyHtml += '<div class="ai-panel__su-content">' + parseRichText(usage.content) + '</div>';
          suBodyHtml += '</li>';
        }
        suBodyHtml += '</ul>';
      }
      if (su.instructions_1 && su.instructions_1.length > 0) {
        suBodyHtml += '<ul class="ai-panel__su-list">';
        for (var ii = 0; ii < su.instructions_1.length; ii++) {
          suBodyHtml += '<li>' + escapeHtml(su.instructions_1[ii]) + '</li>';
        }
        suBodyHtml += '</ul>';
      }
      if (su.instructions_2 && su.instructions_2.length > 0) {
        suBodyHtml += '<ul class="ai-panel__su-list">';
        for (var i2 = 0; i2 < su.instructions_2.length; i2++) {
          suBodyHtml += '<li>' + escapeHtml(su.instructions_2[i2]) + '</li>';
        }
        suBodyHtml += '</ul>';
      }
      if (suBodyHtml) {
        suggestedUsePanelHtml = buildDetailPanel("Suggested use", suBodyHtml);
      }
    }

    var allPanels = ingredientPanelHtml + nutritionPanelHtml + suggestedUsePanelHtml;
    var panelsHtml = allPanels
      ? '<div class="ai-results__item-panels">' + allPanels + '</div>'
      : "";

    li.innerHTML =
      '<div class="ai-results__item-image">' +
        '<img src="' + escapeHtml(product.image_url) + '" alt="' + escapeHtml(product.title) + '">' +
      "</div>" +
      '<div class="ai-results__item-info">' +
        '<h5 class="ai-results__item-title">' + escapeHtml(product.title) + "</h5>" +
        '<div class="ai-results__item-price">' + priceHtml + "</div>" +
                '<div class="ai-results__item-reason"><p class="ai-results__item-reason-label">Why we picked this for you</p><p class="ai-results__item-reason-text">' + escapeHtml(reason) + "</p></div>" +
        highlightsHtml +
        allergensHtml +
        panelsHtml +
      "</div>" +
      '<div class="ai-results__item-action">' +
        '<button type="button" class="ai-results__item-toggle">' +
          (autoSelected ? "Remove" : "Add") +
        "</button>" +
      "</div>" +
      '<div class="ai-results__item-check">✓</div>';

    // Prevent panel interactions from toggling the card's add/remove state,
    // and close sibling panels when one opens
    var panelsEl = li.querySelector(".ai-results__item-panels");
    if (panelsEl) {
      panelsEl.addEventListener("click", function (e) { e.stopPropagation(); });
      var detailEls = panelsEl.querySelectorAll("details");
      for (var di = 0; di < detailEls.length; di++) {
        (function (thisDetail) {
          thisDetail.addEventListener("toggle", function () {
            if (thisDetail.open) {
              for (var dj = 0; dj < detailEls.length; dj++) {
                if (detailEls[dj] !== thisDetail) detailEls[dj].open = false;
              }
            }
          });
        })(detailEls[di]);
      }
    }

    // Toggle handler on the button
    li.querySelector(".ai-results__item-toggle").addEventListener("click", function (e) {
      e.stopPropagation();
      toggleItem(product.handle);
    });

    // Also allow clicking the whole card
    li.addEventListener("click", function () {
      toggleItem(product.handle);
    });

    return li;
  }

  function toggleItem(handle) {
    var idx = selectedItems.indexOf(handle);
    if (idx > -1) {
      selectedItems.splice(idx, 1);
    } else {
      selectedItems.push(handle);
    }

    // Update card visual state
    var cards = document.querySelectorAll('.ai-results__item[data-handle="' + handle + '"]');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var isSelected = selectedItems.indexOf(handle) > -1;
      card.classList.toggle("ai-results__item--selected", isSelected);
      var btn = card.querySelector(".ai-results__item-toggle");
      if (btn) btn.textContent = isSelected ? "Remove" : "Add";
    }

    updateCart();
  }

  // -----------------------------------------------------------------------
  // Cart sidebar
  // -----------------------------------------------------------------------
  function updateCart() {
    var cartItems = document.getElementById("ai-cart-items");
    var cartTotal = document.getElementById("ai-cart-total");
    cartItems.innerHTML = "";

    if (selectedItems.length === 0) {
      cartItems.innerHTML = '<li class="ai-results__cart-empty">Select products above to build your order.</li>';
      cartTotal.style.display = "none";
      return;
    }

    var isSubscription = getOrderMode() === "subscription";
    var total = 0;

    for (var i = 0; i < selectedItems.length; i++) {
      var product = findProduct(selectedItems[i]);
      if (!product) continue;

      var priceNum = product.price_raw ? (product.price_raw / 100) : 0;
      var itemPrice = priceNum;
      if (isSubscription && product.discount_percentage) {
        itemPrice = priceNum * (100 - product.discount_percentage) / 100;
      }
      total += itemPrice;

      var li = document.createElement("li");
      li.className = "ai-results__cart-item";
      li.innerHTML =
        '<img src="' + escapeHtml(product.image_url) + '" alt="">' +
        '<span class="ai-results__cart-item-title">' + escapeHtml(product.title) + "</span>" +
        '<span class="ai-results__cart-item-price">' + buildCartItemPriceHtml(product, isSubscription) + "</span>" +
        '<button type="button" class="ai-results__cart-item-remove" data-handle="' + escapeHtml(product.handle) + '">&times;</button>';

      li.querySelector(".ai-results__cart-item-remove").addEventListener("click", function (e) {
        e.stopPropagation();
        toggleItem(this.getAttribute("data-handle"));
      });

      cartItems.appendChild(li);
    }

    // Total
    cartTotal.style.display = "";
    cartTotal.querySelector(".ai-cart-total__amount").innerHTML = "Order total: £" + total.toFixed(2) + ' <span class="ai-cart-total__per-day">(£' + (total / 30).toFixed(2) + '/day)</span>';
    cartTotal.querySelector(".ai-cart-total__delivery").textContent =
      total > 11 ? "Free delivery on this order" : "£1 delivery. Free on orders over £11";
  }

  function getOrderMode() {
    var checked = document.querySelector('input[name="ai_order_mode"]:checked');
    return checked ? checked.value : "subscription";
  }

  // -----------------------------------------------------------------------
  // Checkout — add selected items to the Shopify cart
  // -----------------------------------------------------------------------
  function handleCheckout(e) {
    e.preventDefault();
    if (selectedItems.length === 0) return;

    var checkoutBtn = document.getElementById("ai-checkout");
    checkoutBtn.textContent = "Please wait...";
    checkoutBtn.style.pointerEvents = "none";

    var isSubscription = getOrderMode() === "subscription";
    var items = [];
    var trackItems = [];

    for (var i = 0; i < selectedItems.length; i++) {
      var product = findProduct(selectedItems[i]);
      if (!product) continue;

      var item = {
        id: product.variant_id,
        quantity: 1,
      };

      if (isSubscription && product.selling_plan_id) {
        item.selling_plan = product.selling_plan_id;
      }

      items.push(item);
      trackItems.push({
        variant_id: product.variant_id,
        quantity: 1,
        selling_plan: item.selling_plan || null,
        handle: selectedItems[i],
      });
    }

    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ items: items }),
    })
      .then(function (res) {
        if (!res.ok) throw new Error("Cart error " + res.status);
        return res.json();
      })
      .then(function () {
        // Attach the recommender token to the cart as a note attribute so the
        // orders/paid webhook can credit this submission with the conversion.
        // Best-effort — never block checkout on it.
        var cartUpdatePromise = resultsToken
          ? fetch("/cart/update.js", {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({
                attributes: { recommender_token: resultsToken },
              }),
            }).catch(function (err) {
              console.error("Recommender token attribute update failed:", err);
            })
          : Promise.resolve();

        // Track the basket add in the worker — fire and forget.
        if (resultsToken && WORKER_URL) {
          fetch(WORKER_URL + "/cart-add", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ token: resultsToken, items: trackItems }),
          }).catch(function () {});
        }

        return cartUpdatePromise;
      })
      .then(function () {
        checkoutBtn.textContent = "Added! ✓";
        checkoutBtn.style.pointerEvents = "";
        if (window.main && window.main.sidebarCart) {
          window.main.sidebarCart.update(true);
        }
      })
      .catch(function (err) {
        console.error("Cart add error:", err);
        checkoutBtn.textContent = "Add all to basket";
        checkoutBtn.style.pointerEvents = "";
        alert("Sorry, there was a problem adding items to your basket. Please try again.");
      });
  }

  // -----------------------------------------------------------------------
  // Results by email — optional, no incentive, skippable. The newsletter is a
  // separate unticked box: the results email is something the customer asked
  // for, and marketing is not its price. The worker fires ONE Klaviyo event
  // carrying the recommended products and a 30-day results link (a Klaviyo
  // flow sends the email) and joins the list only if the box was ticked. Only
  // the address, the choice and the submission token leave this page — the
  // worker re-derives the products from the stored submission, so nothing
  // about the quiz answers is sent from here.
  // -----------------------------------------------------------------------
  function bindEmailCapture() {
    var form = document.getElementById("ai-email-form");
    if (!form) return;

    var skip = document.getElementById("ai-email-skip");
    if (skip) {
      skip.addEventListener("click", function () {
        rememberEmailSkip();
        hideEmailCapture();
      });
    }

    form.addEventListener("submit", function (e) {
      e.preventDefault();
      var input = document.getElementById("ai-email-input");
      var status = document.getElementById("ai-email-status");
      var submit = document.getElementById("ai-email-submit");
      var newsletterBox = document.getElementById("ai-email-newsletter");
      var email = input ? input.value.trim() : "";
      var newsletter = !!(newsletterBox && newsletterBox.checked);

      var showStatus = function (message, isError) {
        if (!status) return;
        status.hidden = false;
        status.textContent = message;
        status.className = "ai-email-capture__status" + (isError ? " ai-email-capture__status--error" : "");
      };

      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        showStatus("Please enter a valid email address.", true);
        return;
      }
      if (!WORKER_URL || !resultsToken) {
        showStatus("Sorry, we can't send your recommendations right now.", true);
        return;
      }

      showStatus("Sending…", false);
      if (submit) submit.disabled = true;

      fetch(WORKER_URL.replace(/\/$/, "") + "/subscribe", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email, token: resultsToken, newsletter: newsletter, images: recommendedImages() }),
      })
        .then(function (res) {
          if (!res.ok) throw new Error("HTTP " + res.status);
          return res.json();
        })
        .then(function () {
          if (status) status.hidden = true;
          hideEmailCapture();
          var confirmed = document.getElementById("ai-email-confirmed");
          if (confirmed) confirmed.hidden = false;
        })
        .catch(function (err) {
          console.error("Results email error:", err);
          showStatus("Sorry, something went wrong. Please try again.", true);
          if (submit) submit.disabled = false;
        });
    });
  }

  // Product pictures for the email, keyed by handle. The worker only accepts
  // Shopify CDN URLs, and only for handles the stored recommendations name, so
  // this is a picture lookup rather than a source of truth.
  function recommendedImages() {
    var out = {};
    if (!recommendations) return out;
    ["highly_recommended", "also_consider"].forEach(function (tier) {
      (recommendations[tier] || []).forEach(function (rec) {
        var p = rec && findProduct(rec.handle);
        if (p && p.image_url) out[rec.handle] = p.image_url;
      });
    });
    return out;
  }

  function hideEmailCapture() {
    var box = document.getElementById("ai-email-capture");
    if (box) box.hidden = true;
  }

  // "No thanks" holds for the session so a refresh doesn't re-ask; a new quiz
  // (new token) asks again.
  function rememberEmailSkip() {
    try {
      if (resultsToken) sessionStorage.setItem("th_rec_email_skip", resultsToken);
    } catch (e) { /* storage unavailable — the box simply reappears on refresh */ }
  }
  function emailSkipped() {
    try {
      return !!resultsToken && sessionStorage.getItem("th_rec_email_skip") === resultsToken;
    } catch (e) {
      return false;
    }
  }

  function resetEmailCaptureUI() {
    var box = document.getElementById("ai-email-capture");
    if (box) box.hidden = emailSkipped();
    var form = document.getElementById("ai-email-form");
    if (form) form.reset();
    var submit = document.getElementById("ai-email-submit");
    if (submit) submit.disabled = false;
    var status = document.getElementById("ai-email-status");
    if (status) {
      status.hidden = true;
      status.textContent = "";
      status.className = "ai-email-capture__status";
    }
    var confirmed = document.getElementById("ai-email-confirmed");
    if (confirmed) confirmed.hidden = true;
  }

  // -----------------------------------------------------------------------
  // Results interaction bindings
  // -----------------------------------------------------------------------
  function bindResultsInteractions() {
    // Checkout
    var checkoutBtn = document.getElementById("ai-checkout");
    if (checkoutBtn) checkoutBtn.addEventListener("click", handleCheckout);

    // Order mode toggle
    var modeInputs = document.querySelectorAll('input[name="ai_order_mode"]');
    for (var i = 0; i < modeInputs.length; i++) {
      modeInputs[i].addEventListener("change", function () {
        refreshItemPriceDisplays();
        updateCart();
      });
    }

    // Copy support reference ID
    var copyBtn = document.getElementById("ai-results-support-copy");
    if (copyBtn) copyBtn.addEventListener("click", handleSupportCopy);
  }

  function handleSupportCopy() {
    if (!resultsToken) return;
    var btn = document.getElementById("ai-results-support-copy");
    var label = btn && btn.querySelector(".ai-results__support-copy-label");
    var done = function () {
      if (!label) return;
      var original = label.textContent;
      label.textContent = "Copied";
      btn.classList.add("ai-results__support-copy--copied");
      setTimeout(function () {
        label.textContent = original;
        btn.classList.remove("ai-results__support-copy--copied");
      }, 2000);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(resultsToken).then(done, fallbackCopy);
    } else {
      fallbackCopy();
    }
    function fallbackCopy() {
      var ta = document.createElement("textarea");
      ta.value = resultsToken;
      ta.setAttribute("readonly", "");
      ta.style.position = "absolute";
      ta.style.left = "-9999px";
      document.body.appendChild(ta);
      ta.select();
      try { document.execCommand("copy"); done(); } catch (e) { /* no-op */ }
      document.body.removeChild(ta);
    }
  }

  // -----------------------------------------------------------------------
  // Reset / restart
  // -----------------------------------------------------------------------
  function resetAll() {
    profile = { health_confirmed: null, gender: null, age_range: null, life_stage: null, menopause_periods_stopped: null, menopause_symptoms_now: null, diet: null, diet_other: "", fruit_veg_portions: null, eats_dairy: null, oily_fish_per_week: null, health_goals: [], symptoms: [], nutrient_concerns: [], current_supplements: "", free_text: "", order_mode: "subscription" };
    selectedItems = [];
    stepHistory = [];
    recommendations = null;
    resultsToken = null;
    resultsRendered = false;
    resultsViewedSent = false;
    deleteCookie("th_rec");

    // Reset textareas
    var ta = document.getElementById("ai-quiz-freetext");
    if (ta) ta.value = "";
    var suppsTa = document.getElementById("ai-quiz-current-supps");
    if (suppsTa) suppsTa.value = "";
    var dietOtherResetTa = document.getElementById("ai-quiz-diet-other");
    if (dietOtherResetTa) dietOtherResetTa.value = "";

    // Reset email capture UI and the expired-link notice
    resetEmailCaptureUI();
    var expiredNotice = document.getElementById("ai-results-expired");
    if (expiredNotice) expiredNotice.hidden = true;

    // Reset terms acceptance step
    var termsCbReset = document.getElementById("ai-terms-checkbox");
    if (termsCbReset) termsCbReset.checked = false;
    var termsBtnReset = document.getElementById("ai-terms-continue");
    if (termsBtnReset) termsBtnReset.disabled = true;
    consentId = null;
    consentPromise = null;

    // Reset disclaimer UI
    var disclaimerEl = document.getElementById("ai-disclaimer");
    if (disclaimerEl) disclaimerEl.classList.add("ai-recommender__disclaimer--hidden");
    var disclaimerCb = document.getElementById("ai-disclaimer-checkbox");
    if (disclaimerCb) disclaimerCb.checked = false;
    var disclaimerBtn = document.getElementById("ai-disclaimer-continue");
    if (disclaimerBtn) disclaimerBtn.disabled = true;

    // Reset health goal buttons
    var goalBtns = document.querySelectorAll(".ai-quiz__btn--goal");
    for (var g = 0; g < goalBtns.length; g++) {
      goalBtns[g].classList.remove("ai-quiz__btn--goal-selected");
    }

    // Reset nutrient concern buttons
    var nutBtns = document.querySelectorAll(".ai-quiz__btn--nutrient");
    for (var n = 0; n < nutBtns.length; n++) {
      nutBtns[n].classList.remove("ai-quiz__btn--goal-selected");
    }

    // Reset symptom buttons
    var symptomResetBtns = document.querySelectorAll(".ai-quiz__btn--symptom");
    for (var sr = 0; sr < symptomResetBtns.length; sr++) {
      symptomResetBtns[sr].classList.remove("ai-quiz__btn--goal-selected");
    }

    // Show quiz, hide results
    document.getElementById("ai-quiz").style.display = "";
    document.getElementById("ai-quiz-header").style.display = "";
    document.getElementById("ai-results").classList.add("ai-recommender__results--hidden");

    showStep("health_confirmation");

    // Scroll to top
    var quizEl = document.getElementById("ai-quiz");
    if (quizEl) quizEl.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  // -----------------------------------------------------------------------
  // Helpers
  // -----------------------------------------------------------------------
  function findProduct(handle) {
    for (var i = 0; i < products.length; i++) {
      if (products[i].handle === handle) return products[i];
    }
    return null;
  }

  // How many recommended products across all sections actually resolve to a
  // product we can render. A response where this is 0 (empty arrays, or only
  // handles that don't exist in the catalogue) must be treated as a failure,
  // never rendered as an empty "results" page.
  function countResolvable(data) {
    if (!data) return 0;
    var groups = ["highly_recommended", "also_consider", "together_alternatives"];
    var n = 0;
    for (var g = 0; g < groups.length; g++) {
      var arr = data[groups[g]];
      if (arr && arr.length) {
        for (var i = 0; i < arr.length; i++) {
          if (arr[i] && findProduct(arr[i].handle)) n++;
        }
      }
    }
    return n;
  }

  function scrollToResults() {
    var el = document.getElementById("ai-results");
    if (el) {
      setTimeout(function () {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    }
  }

  function escapeHtml(str) {
    if (!str) return "";
    return String(str)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  }

  // -----------------------------------------------------------------------
  // Cookie helpers
  // -----------------------------------------------------------------------
  function setCookie(name, value, days) {
    var maxAge = days * 24 * 60 * 60;
    document.cookie = name + "=" + encodeURIComponent(value) + "; path=/; max-age=" + maxAge + "; SameSite=Lax; Secure";
  }

  function getCookie(name) {
    var match = document.cookie.match(new RegExp("(?:^|; )" + name.replace(/[.*+?^${}()|[\]\\]/g, "\\$&") + "=([^;]*)"));
    return match ? decodeURIComponent(match[1]) : null;
  }

  function deleteCookie(name) {
    document.cookie = name + "=; path=/; max-age=0; SameSite=Lax; Secure";
  }

  // -----------------------------------------------------------------------
  // Boot
  // -----------------------------------------------------------------------
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
